/**
 * Markdown renderer for chat messages.
 *
 * Block-first, then inline. The previous version ran inline rules over the
 * whole string before extracting code blocks, so a `*` inside a fenced block
 * became <em> and every newline became <br> — which is why pre-formatted
 * output rendered as a run-on wall of text. Fenced blocks and tables are now
 * pulled out first and never see the inline pass.
 *
 * Supports: headers, bold, italic, inline code, fenced code, tables,
 * blockquotes, ordered and unordered lists, horizontal rules, links.
 */

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Inline formatting. Never applied to code or table-structure characters. */
function inline(text) {
  let out = text;

  // Inline code first, so its contents are shielded from the rules below.
  const codes = [];
  out = out.replace(/`([^`\n]+)`/g, (_, code) => {
    codes.push(code);
    return `\u0001CODE${codes.length - 1}\u0001`;
  });

  // Links: [label](url). Only http(s) to avoid javascript: URLs.
  out = out.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  // Single * / _ for italics, but not inside words (snake_case, 3*4).
  out = out.replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s).,;:!?]|$)/g, '$1<em>$2</em>');
  out = out.replace(/(^|[\s(])_([^_\n]+)_(?=[\s).,;:!?]|$)/g, '$1<em>$2</em>');

  // U+0001 is a deliberate sentinel: it cannot appear in the escaped
  // input, so it safely marks extracted code spans for reinsertion.
  // eslint-disable-next-line no-control-regex
  out = out.replace(/\u0001CODE(\d+)\u0001/g, (_, i) => `<code>${codes[Number(i)]}</code>`);
  return out;
}

/** A GitHub-style table: header row, a |---|---| separator, then body rows. */
function isTableSeparator(line) {
  return /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(line);
}

function splitRow(line) {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());
}

/** Column alignment from the separator row (:--, :-:, --:). */
function alignmentsFrom(sep) {
  return splitRow(sep).map((c) => {
    const left = c.startsWith(':');
    const right = c.endsWith(':');
    if (left && right) return 'center';
    if (right) return 'right';
    return 'left';
  });
}

function renderTable(rows) {
  const [head, sep, ...body] = rows;
  const aligns = alignmentsFrom(sep);
  const cell = (text, i, tag) => {
    const align = aligns[i] && aligns[i] !== 'left' ? ` style="text-align:${aligns[i]}"` : '';
    return `<${tag}${align}>${inline(text)}</${tag}>`;
  };

  const thead = `<thead><tr>${splitRow(head)
    .map((c, i) => cell(c, i, 'th'))
    .join('')}</tr></thead>`;

  const tbody = body.length
    ? `<tbody>${body
        .map(
          (r) =>
            `<tr>${splitRow(r)
              .map((c, i) => cell(c, i, 'td'))
              .join('')}</tr>`
        )
        .join('')}</tbody>`
    : '';

  // Wrapped so a wide table scrolls inside its own box rather than stretching
  // the message bubble — the failure visible on narrow screens.
  return `<div class="table-wrap"><table>${thead}${tbody}</table></div>`;
}

export function parseMarkdown(text) {
  if (!text) return '';

  const lines = escapeHtml(text).split('\n');
  const out = [];
  let i = 0;

  const flushParagraph = (buf) => {
    if (buf.length) {
      out.push(`<p>${inline(buf.join('<br>'))}</p>`);
      buf.length = 0;
    }
  };
  const para = [];

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block — taken verbatim, no inline processing.
    const fence = line.match(/^\s*```(\w*)\s*$/);
    if (fence) {
      flushParagraph(para);
      const body = [];
      i += 1;
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i])) {
        body.push(lines[i]);
        i += 1;
      }
      i += 1; // closing fence
      const lang = fence[1] ? ` class="lang-${fence[1]}"` : '';
      out.push(`<pre><code${lang}>${body.join('\n')}</code></pre>`);
      continue;
    }

    // Table
    if (line.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      flushParagraph(para);
      const rows = [line, lines[i + 1]];
      i += 2;
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        rows.push(lines[i]);
        i += 1;
      }
      out.push(renderTable(rows));
      continue;
    }

    // Heading
    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      flushParagraph(para);
      const level = heading[1].length;
      out.push(`<h${level}>${inline(heading[2])}</h${level}>`);
      i += 1;
      continue;
    }

    // Horizontal rule
    if (/^\s*([-*_])\s*\1\s*\1[\s\-*_]*$/.test(line)) {
      flushParagraph(para);
      out.push('<hr>');
      i += 1;
      continue;
    }

    // Blockquote
    if (/^\s*&gt;\s?/.test(line)) {
      flushParagraph(para);
      const body = [];
      while (i < lines.length && /^\s*&gt;\s?/.test(lines[i])) {
        body.push(lines[i].replace(/^\s*&gt;\s?/, ''));
        i += 1;
      }
      out.push(`<blockquote>${inline(body.join('<br>'))}</blockquote>`);
      continue;
    }

    // Lists (ordered or unordered), including simple nesting by indent.
    const bullet = line.match(/^(\s*)([-*+]|\d+\.)\s+(.*)$/);
    if (bullet) {
      flushParagraph(para);
      const ordered = /\d/.test(bullet[2]);
      const tag = ordered ? 'ol' : 'ul';
      const items = [];
      let baseIndent = bullet[1].length;
      while (i < lines.length) {
        const m = lines[i].match(/^(\s*)([-*+]|\d+\.)\s+(.*)$/);
        if (!m) break;
        const isOrdered = /\d/.test(m[2]);
        if (isOrdered !== ordered && m[1].length <= baseIndent) break;
        const indent = m[1].length;
        if (indent > baseIndent) {
          // Nested item: append to the previous <li>.
          const nested = [];
          while (i < lines.length) {
            const n = lines[i].match(/^(\s*)([-*+]|\d+\.)\s+(.*)$/);
            if (!n || n[1].length <= baseIndent) break;
            nested.push(`<li>${inline(n[3])}</li>`);
            i += 1;
          }
          const nestedTag = ordered ? 'ol' : 'ul';
          if (items.length) {
            items[items.length - 1] = items[items.length - 1].replace(
              /<\/li>$/,
              `<${nestedTag}>${nested.join('')}</${nestedTag}></li>`
            );
          } else {
            items.push(`<li><${nestedTag}>${nested.join('')}</${nestedTag}></li>`);
          }
          continue;
        }
        baseIndent = indent;
        items.push(`<li>${inline(m[3])}</li>`);
        i += 1;
      }
      out.push(`<${tag}>${items.join('')}</${tag}>`);
      continue;
    }

    // Blank line ends a paragraph.
    if (!line.trim()) {
      flushParagraph(para);
      i += 1;
      continue;
    }

    para.push(line);
    i += 1;
  }

  flushParagraph(para);
  return out.join('');
}

export default parseMarkdown;
